import React, { useEffect, useState } from 'react'
import { Languages, Loader2, ExternalLink } from 'lucide-react'
import { englishContentService } from '../../../services/english/englishContentService'
import { genId } from '../lib/id'
import type { EnglishCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const ENG_BASE = 'https://eng.vatli365.vn'
const engUrl = (c: EnglishCard) => `${ENG_BASE}/physics-english/${c.lop}/${c.chuong}/${c.bai}`

// Thẻ Tiếng Anh Vật lí: chọn bài → nút "Học ngay" mở bài tương ứng trên eng.vatli365.vn.
const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as EnglishCard
  const [lessons, setLessons] = useState<any[] | null>(null)

  useEffect(() => {
    englishContentService.getAllLessons().then((l) => setLessons(l as any[])).catch(() => setLessons([]))
  }, [])

  const cur = c.lop != null ? `${c.lop}-${c.chuong}-${c.bai}` : ''

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Chọn bài Tiếng Anh Vật lí</label>
        {lessons === null ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-2"><Loader2 size={14} className="animate-spin" /> Đang tải danh sách...</div>
        ) : (
          <select
            className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 bg-white"
            value={cur}
            onChange={(e) => {
              if (!e.target.value) {
                onChange({ lop: undefined, chuong: undefined, bai: undefined, engTitle: undefined } as Partial<EnglishCard>)
                return
              }
              const [lop, chuong, bai] = e.target.value.split('-').map(Number)
              const les = (lessons || []).find((l) => l.lop === lop && l.chuong === chuong && l.bai === bai)
              onChange({ lop, chuong, bai, engTitle: les?.title } as Partial<EnglishCard>)
            }}
          >
            <option value="">— Chọn bài —</option>
            {(lessons || []).map((l) => (
              <option key={`${l.lop}-${l.chuong}-${l.bai}`} value={`${l.lop}-${l.chuong}-${l.bai}`}>
                L{l.lop}·C{l.chuong}·B{l.bai} — {l.title || 'Bài học'}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="text-[11px] text-gray-400">Khi học, bấm “Học ngay” sẽ mở bài tương ứng trên eng.vatli365.vn.</p>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as EnglishCard
  if (c.lop == null || c.chuong == null || c.bai == null) {
    return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa chọn bài Tiếng Anh.</div>
  }
  return (
    <a
      href={engUrl(c)}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50/60 hover:bg-rose-50 transition-colors group"
    >
      <span className="w-11 h-11 rounded-xl bg-rose-500 text-white flex items-center justify-center shrink-0">
        <Languages size={22} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[11px] font-black uppercase tracking-wide text-rose-600">Tiếng Anh Vật lí</span>
        <span className="block text-base font-bold text-gray-800 truncate">{c.engTitle || `Lớp ${c.lop} · Chương ${c.chuong} · Bài ${c.bai}`}</span>
        <span className="block text-xs text-gray-400">Mở bài học trên eng.vatli365.vn</span>
      </span>
      <span className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-bold group-hover:bg-rose-700">
        Học ngay <ExternalLink size={16} />
      </span>
    </a>
  )
}

export const englishCardDef: CardDef = {
  type: 'english',
  label: 'Tiếng Anh Vật lí',
  description: 'Mở bài trên eng.vatli365.vn',
  icon: Languages,
  color: 'bg-rose-50 text-rose-600',
  createDefault: (): EnglishCard => ({ id: genId(), type: 'english' }),
  Editor,
  Preview,
}
